import type { components } from '@feedfm/clips-services-api';
import { normalizeTrackKey } from '../../../util/normalizeTrackKey';

type SimilarTrack = components['schemas']['SimilarTrack'];
type TrackObject = components['schemas']['TrackObject'];

// Re-exported for existing consumers/tests of this module — the shared
// implementation now also backs SimilarTracksModal's own normalization.
export { normalizeTrackKey };

/** First catalog candidate whose normalized title+artist equals the similarity result, else null. */
export const pickCatalogMatch = (
  result: SimilarTrack,
  candidates: TrackObject[],
): TrackObject | null => {
  const key = normalizeTrackKey(result.title, result.displayArtistName);
  return (
    candidates.find(
      (c) => normalizeTrackKey(c.title, c.display_artist_name) === key,
    ) ?? null
  );
};

/**
 * Resolve a rendered catalog row's trackId back to its raw catalog TrackObject.
 *
 * The Catalog table renders from one of two sources depending on mode: the
 * current catalog page normally, or the Find Similar results while a similarity
 * view is active. Anything that acts on a clicked row (playback, "find similar")
 * must look the raw track up in whichever source produced the rows on screen —
 * resolving against the catalog page alone silently misses similarity results,
 * which generally aren't on the current page.
 */
export const findRenderedTrack = (
  trackId: number,
  sources: {
    similarityActive: boolean;
    similarResults: TrackObject[];
    catalogTracks: TrackObject[];
  },
): TrackObject | null => {
  const rows = sources.similarityActive
    ? sources.similarResults
    : sources.catalogTracks;
  return rows.find((t) => t.id === trackId) ?? null;
};

/**
 * Turn similarity results + their per-result catalog candidate lists into the
 * final row list: similarity order preserved, seed excluded, deduped by catalog
 * id, misses skipped.
 */
export const hydrateSimilarResults = (args: {
  results: SimilarTrack[];
  candidatesByIndex: TrackObject[][];
  seedTrackId: number;
}): TrackObject[] => {
  const { results, candidatesByIndex, seedTrackId } = args;
  const seen = new Set<number>();
  const out: TrackObject[] = [];
  results.forEach((result, i) => {
    const match = pickCatalogMatch(result, candidatesByIndex[i] ?? []);
    if (!match) return;
    if (match.id === seedTrackId) return; // Intentional: exclude by catalog id (more precise than SimilarTracksModal's title/URL dedupe).
    if (seen.has(match.id)) return;
    seen.add(match.id);
    out.push(match);
  });
  return out;
};
